/**
 * RemediesPage — personalised Vedic remedies (उपाय).
 *
 * Features:
 *  - Personalised remedy list sorted by priority (high → medium → low)
 *  - Category filter: Gemstones / Mantras / Yantras / Rituals / Charitable Acts
 *  - Expandable remedy cards with description, when-to-perform, duration
 *  - Mark as done (local state only, no persistence required for MVP)
 */
import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  remediesApi,
  type Remedy,
  type RemedyCategory,
} from '@/lib/api'
import { cn } from '@/lib/utils'
import { useFeatureGate } from '@/hooks/useSubscription'
import { HardGate } from '@/components/paywall'
import { SkeletonCard } from '@/components/ui'
import {
  RefreshCw, ChevronDown, ChevronUp, CheckCircle2, Circle,
  Gem, Music2, Grid3X3, Flame, HandHeart, Star,
} from 'lucide-react'

// ── Category config ───────────────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<RemedyCategory, {
  label: string; sanskrit: string
  icon: React.ReactNode; bigIcon: React.ReactNode
  color: string; bg: string; border: string
}> = {
  gemstone: { label: 'Gemstones', sanskrit: 'रत्न',      icon: <Gem       size={13} />, bigIcon: <Gem       size={20} />, color: 'text-violet-glow', bg: 'bg-violet/10',     border: 'border-violet/20'    },
  mantra:   { label: 'Mantras',   sanskrit: 'मंत्र',     icon: <Music2    size={13} />, bigIcon: <Music2    size={20} />, color: 'text-gold',         bg: 'bg-gold/10',        border: 'border-gold/20'      },
  yantra:   { label: 'Yantras',   sanskrit: 'यंत्र',     icon: <Grid3X3   size={13} />, bigIcon: <Grid3X3   size={20} />, color: 'text-auspicious',   bg: 'bg-auspicious/10',  border: 'border-auspicious/20'},
  ritual:   { label: 'Rituals',   sanskrit: 'अनुष्ठान',  icon: <Flame     size={13} />, bigIcon: <Flame     size={20} />, color: 'text-inauspicious', bg: 'bg-inauspicious/10',border: 'border-inauspicious/20'},
  charity:  { label: 'Charity',   sanskrit: 'दान',        icon: <HandHeart size={13} />, bigIcon: <HandHeart size={20} />, color: 'text-star-soft',   bg: 'bg-star-dim/10',    border: 'border-star-dim/20'  },
}

const ALL_CATEGORIES: RemedyCategory[] = ['gemstone', 'mantra', 'yantra', 'ritual', 'charity']

const PRIORITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 }

// ── Priority badge ────────────────────────────────────────────────────────────

function PriorityBadge({ priority }: { priority: Remedy['priority'] }) {
  const styles = {
    high:   'bg-inauspicious/10 text-inauspicious border-inauspicious/20',
    medium: 'bg-gold/10 text-gold border-gold/20',
    low:    'bg-star-dim/10 text-star-dim border-star-dim/20',
  }
  const labels = { high: 'High', medium: 'Medium', low: 'Low' }
  return (
    <span className={cn(
      'inline-flex items-center px-2 py-0.5 rounded-full text-2xs font-medium border',
      styles[priority],
    )}>
      {labels[priority]}
    </span>
  )
}

// ── Remedy card ───────────────────────────────────────────────────────────────

function RemedyCard({
  remedy, done, onToggleDone,
}: {
  remedy: Remedy
  done: boolean
  onToggleDone: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  const cfg = CATEGORY_CONFIG[remedy.category]

  return (
    <div className={cn(
      'card-cosmic overflow-hidden transition-all duration-200',
      done && 'opacity-55',
    )}>
      {/* Header row */}
      <div className="flex items-start gap-3 p-4">
        {/* Category icon badge */}
        <div className={cn(
          'flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center border',
          cfg.bg, cfg.border, cfg.color,
        )}>
          {cfg.bigIcon}
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-0.5">
            <h3 className={cn(
              'text-sm font-medium text-star-bright',
              done && 'line-through',
            )}>
              {remedy.title}
            </h3>
            <PriorityBadge priority={remedy.priority} />
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <span className={cn('inline-flex items-center gap-1 text-2xs font-mono', cfg.color)}>
              {cfg.icon} {cfg.label}
              <span className="font-display text-2xs opacity-60 ml-0.5">{cfg.sanskrit}</span>
            </span>
            {remedy.planet && (
              <>
                <span className="text-star-dim/40 text-2xs">·</span>
                <span className="text-2xs text-star-dim font-mono flex items-center gap-0.5">
                  <Star size={9} className="text-gold-dim/60" />
                  {remedy.planet}
                </span>
              </>
            )}
          </div>

          <p className="text-xs text-star-muted leading-relaxed">{remedy.purpose}</p>
        </div>

        {/* Action buttons */}
        <div className="flex-shrink-0 flex items-center gap-1">
          <button
            onClick={onToggleDone}
            className="p-1.5 rounded-lg text-star-dim hover:text-auspicious transition-colors hover:bg-auspicious/10"
            aria-label={done ? 'Mark undone' : 'Mark as done'}
          >
            {done
              ? <CheckCircle2 size={16} className="text-auspicious" />
              : <Circle size={16} />}
          </button>
          <button
            onClick={() => setExpanded(e => !e)}
            className="p-1.5 rounded-lg text-star-dim hover:text-star-bright hover:bg-cosmic-void transition-colors"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-cosmic-mist/50">
          {/* Steps for ritual category — presented as numbered steps */}
          {remedy.category === 'ritual' ? (
            <div className="px-4 pb-4 pt-3 space-y-3">
              <p className="text-2xs font-mono uppercase tracking-wider text-star-dim">Instructions</p>
              <ol className="space-y-2">
                {remedy.description.split(/[.;]\s+/).filter(s => s.trim().length > 8).map((step, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-inauspicious/15 text-inauspicious text-2xs font-bold flex items-center justify-center mt-0.5">
                      {i + 1}
                    </span>
                    <p className="text-xs text-star-muted leading-relaxed">{step.trim()}{step.endsWith('.') ? '' : '.'}</p>
                  </li>
                ))}
              </ol>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div className="bg-cosmic-void/60 rounded-lg px-3 py-2">
                  <p className="text-2xs font-mono uppercase tracking-wider text-star-dim mb-1">When</p>
                  <p className="text-xs text-star">{remedy.whenToPerform}</p>
                </div>
                <div className="bg-cosmic-void/60 rounded-lg px-3 py-2">
                  <p className="text-2xs font-mono uppercase tracking-wider text-star-dim mb-1">Duration</p>
                  <p className="text-xs text-star">{remedy.duration}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="px-4 pb-4 pt-3 space-y-3">
              <div>
                <p className="text-2xs font-mono uppercase tracking-wider text-star-dim mb-1">Details</p>
                <p className="text-xs text-star-muted leading-relaxed">{remedy.description}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="bg-cosmic-void/60 rounded-lg px-3 py-2">
                  <p className="text-2xs font-mono uppercase tracking-wider text-star-dim mb-1">When to Perform</p>
                  <p className="text-xs text-star">{remedy.whenToPerform}</p>
                </div>
                <div className="bg-cosmic-void/60 rounded-lg px-3 py-2">
                  <p className="text-2xs font-mono uppercase tracking-wider text-star-dim mb-1">Duration</p>
                  <p className="text-xs text-star">{remedy.duration}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function RemediesPage() {
  const gate = useFeatureGate('remedies')
  const [activeCategory, setActiveCategory] = useState<RemedyCategory | 'all'>('all')
  const [doneIds, setDoneIds] = useState<Set<string>>(new Set())

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['remedies'],
    queryFn: () => remediesApi.getAll(),
    retry: 1,
  })

  const remedies = data ?? []

  const filtered = useMemo(() => {
    const base = activeCategory === 'all'
      ? remedies
      : remedies.filter(r => r.category === activeCategory)
    return [...base].sort(
      (a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority],
    )
  }, [remedies, activeCategory])

  const doneCount = doneIds.size
  const totalCount = remedies.length

  function toggleDone(id: string) {
    setDoneIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  if (!gate.isLoading && !gate.allowed) {
    return (
      <div className="max-w-3xl mx-auto space-y-6 p-4 sm:p-6">
        <div>
          <p className="text-2xs font-display text-gold-dim tracking-widest mb-1">उपाय</p>
          <h1 className="text-xl font-bold text-star-bright">Remedies</h1>
        </div>
        <div className="card-cosmic">
          <HardGate
            feature="Remedies"
            unlockDescription="Personalised Vedic remedies are a Premium feature. Upgrade to unlock your remedy recommendations."
          />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 p-4 sm:p-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-2xs font-display text-gold-dim tracking-widest mb-1">उपाय</p>
          <h1 className="text-xl font-bold text-star-bright">Remedies</h1>
          <p className="text-xs text-star-muted mt-0.5">
            Personalised Vedic remedies to strengthen planetary influences
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className={cn(
            'p-2 rounded-lg border border-cosmic-mist text-star-dim hover:text-star-bright hover:border-violet-dark transition-colors',
            isLoading && 'animate-spin',
          )}
          aria-label="Refresh remedies"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      <HardGate locked={!gate.allowed} featureName="Remedies">
      {/* Progress bar */}
      <div className="card-cosmic p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-star-muted">Completed</span>
          <span className="text-xs font-mono text-gold-dim">{doneCount} / {totalCount}</span>
        </div>
        <div className="h-2 bg-cosmic-void rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-gold to-auspicious rounded-full transition-all duration-500"
            style={{ width: totalCount > 0 ? `${(doneCount / totalCount) * 100}%` : '0%' }}
          />
        </div>
        {doneCount === totalCount && totalCount > 0 && (
          <p className="mt-2 text-xs text-auspicious text-center">✦ All remedies completed ✦</p>
        )}
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} className="h-20" />
          ))}
        </div>
      )}

      {isError && (
        <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-inauspicious/10 border border-inauspicious/20">
          <span className="text-xs text-inauspicious">Could not load remedies. Please try again.</span>
          <button onClick={() => refetch()} className="text-xs text-gold hover:text-gold-bright transition-colors">Retry</button>
        </div>
      )}

      {!isLoading && !isError && remedies.length === 0 && (
        <div className="card-cosmic p-6 text-center text-star-dim text-sm">
          No remedies available at this time.
        </div>
      )}

      {/* Category filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setActiveCategory('all')}
          className={cn(
            'px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
            activeCategory === 'all'
              ? 'bg-cosmic-dusk border-violet-dark text-star-bright'
              : 'bg-cosmic-void border-cosmic-mist text-star-dim hover:border-violet-dark hover:text-star-bright',
          )}
        >
          All
          <span className="ml-1.5 font-mono text-2xs text-gold-dim">{remedies.length}</span>
        </button>
        {ALL_CATEGORIES.map(cat => {
          const cfg = CATEGORY_CONFIG[cat]
          const count = remedies.filter(r => r.category === cat).length
          if (count === 0) return null
          return (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
                activeCategory === cat
                  ? 'bg-cosmic-dusk border-violet-dark text-star-bright'
                  : 'bg-cosmic-void border-cosmic-mist text-star-dim hover:border-violet-dark hover:text-star-bright',
              )}
            >
              <span className={activeCategory === cat ? 'text-gold' : 'text-star-dim'}>{cfg.icon}</span>
              {cfg.label}
              <span className="font-mono text-2xs text-gold-dim">{count}</span>
            </button>
          )
        })}
      </div>

      {/* Remedy list */}
      {filtered.length === 0 ? (
        <div className="card-cosmic p-8 text-center">
          <p className="text-star-dim text-sm">No remedies in this category.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(remedy => (
            <RemedyCard
              key={remedy.id}
              remedy={remedy}
              done={doneIds.has(remedy.id)}
              onToggleDone={() => toggleDone(remedy.id)}
            />
          ))}
        </div>
      )}
      </HardGate>
    </div>
  )
}
